import { all, takeLatest, put, fork } from 'redux-saga/effects';
import { stopSubmit } from 'redux-form';
import { getSessionFlow } from 'containers/App/sagas';
import { push } from 'react-router-redux'
import {
  FETCH_CHANNELS_REQUEST,
  FETCH_CHANNEL_DETAIL_REQUEST,
  FETCH_UPDATED_CHANNEL_DETAIL_REQUEST,
  FETCH_SLUG_VALIDATE_REQUEST,
  FETCH_CLIENTS_REQUEST,
  FETCH_THEMES_REQUEST,
  FETCH_MODES_REQUEST,
  POST_CHANNEL_REQUEST,
  PUT_CHANNEL_SUCCESS,
  PATCH_CHANNEL_SUCCESS,
  PUT_CHANNEL_FAILURE,
  PATCH_CHANNEL_REQUEST,
  DELETE_CHANNEL_REQUEST,
  DELETE_CHANNEL_SUCCESS,
} from './constants';
import {
  fetchChannelsSuccess,
  fetchChannelsFailure,
  patchChannelSuccess,
  putChannelSuccess,
  putChannelFailure,
  fetchClientsSuccess,
  fetchClientsFailure,
  fetchThemesSuccess,
  fetchThemesFailure,
  fetchModesSuccess,
  fetchModesFailure,
  validateChannelSlugFailure,
  validateChannelSlugSuccess,
  deleteChannelSuccess,
  deleteChannelFailure,
  fetchChannelDetailSuccess
} from './actions';
import { authedApiSaga } from 'sagas';
import {
  getChannels,
  postChannel,
  getClients,
  patchChannel,
  formPatchChannel,
  getChannelThemes,
  getModes,
  validateChannel,
  deleteChannel,
  getChannelDetail,
} from 'utils/api';
import { isString, isObject } from 'utils/helpers';

const getFetchParams = (params) => {
  return params
    ? `?${Object.keys(params).map(k => params[k] ? `${k}=${params[k]}` : null).filter(x => x).join('&')}`
    : '';
}

const getUpdatedBrandParams = ({
  big_logo_image_url,
  overlay_image,
  gallery_banner,
  photo_filters,
}) => {
  const app_logo_image = isObject(big_logo_image_url)
    ? big_logo_image_url[0]
    : big_logo_image_url === ""
    ? null
    : undefined;
  const app_background_image = isObject(overlay_image)
    ? overlay_image[0]
    : overlay_image === ""
    ? null
    : undefined;
  const gallery_logo = isObject(gallery_banner)
    ? gallery_banner[0]
    : gallery_banner === ""
    ? null
    : undefined;
  const photo_filters_img = isObject(photo_filters)
    ? Array.isArray(photo_filters)
      ? photo_filters.length == 0
        ? null
        : photo_filters.filter((x) => x).map((x) => x.file)
      : photo_filters[0]
    : undefined;

  return {
    big_logo_image_url: app_logo_image,
    overlay_image: app_background_image,
    gallery_banner: gallery_logo,
    photo_filters: photo_filters_img,
  };
};

export function* fetchChannelsFlow({ params }) {
  yield* authedApiSaga(token => getChannels(token, getFetchParams(params)), [], fetchChannelsSuccess, fetchChannelsFailure);
}

export function* fetchChannelDetailFlow({ id }) {
  yield* authedApiSaga(token => getChannelDetail(token, id), {}, fetchChannelDetailSuccess, fetchChannelsFailure);
}

export function* fetchUpdatedChannelDetailFlow({ id }) {
  yield* authedApiSaga(token => getChannelDetail(token, id), {}, fetchChannelDetailSuccess, fetchChannelsFailure);
}

export function* validateChannelSlugRequestFlow( {id} ) {
  yield* authedApiSaga(token => validateChannel(token, id), {}, validateChannelSlugSuccess, validateChannelSlugFailure);
}
export function* validateChannelSlugSuccessFlow({ payload }) {
  yield put(validateChannelSlugSuccess(payload));
}

export function* validateChannelSlugFailureFlow({ payload }) {
  yield put(validateChannelSlugFailure(payload));
}

export function* postChannelFlow({ payload }) {
  const clientData = payload.client;
  const client_id =
    isString(clientData) ? undefined : clientData ? clientData : undefined;

  const args = [{
    client_name: isString(clientData) ? clientData : undefined,
    client_id,
    ...payload,
    ...getUpdatedBrandParams(payload),
  }];
  yield* authedApiSaga(postChannel, args, putChannelSuccess, putChannelFailure);
  yield fork(getSessionFlow);
}

export function* patchChannelFlow({ payload: { id, data } }) {
  const {
    client: clientData,
    hashtag_image,
    hashtag_image_url,
    ...rest
  } = data;

  yield* authedApiSaga(
    patchChannel,
    [
      id,
      {
        ...rest,
        client_name: isString(clientData) ? clientData : undefined,
        client_id: isString(clientData) ? undefined : clientData,
        ...getUpdatedBrandParams(rest),
      },
    ],
    patchChannelSuccess,
    putChannelFailure
  );
}

export function* channelSuccessFlowForPost({ payload: { allChannels } }) {
  // Pattern match on the first and only id returned
  const [id]       = Object.keys(allChannels),
        nextRoute  = `/channels`;
  yield all([
    put(push(nextRoute)),
  ]);
}
export function* channelSuccessFlowForPatch({ payload: { allChannels }}) {
  // Pattern match on the first and only id returned
  const [id]       = Object.keys(allChannels),
        nextRoute  = `/channels/${id}/edit`;
  yield all([
    put(push(nextRoute)),
  ]);
}

export function* channelFailureFlow({ payload: { errors }}) {
  yield all([
    put(stopSubmit('channelForm', errors)),
    // put(setModal({ modalType: PUT_CHANNEL_ERROR, modalProps: { ...errors }})),
  ]);
}

export function* deleteChannelSuccessFlow() {

  yield all([
    put(push('/'))
  ]);
}

export function* deleteChannelFlow( {channelId} ) {
  yield* authedApiSaga(deleteChannel, [
    { channelId }
  ], deleteChannelSuccess, deleteChannelFailure);
}

export function* fetchClientsFlow({ payload: { q } }) {
  yield* authedApiSaga(getClients, [
    { q }
  ], fetchClientsSuccess, fetchClientsFailure);
}

export function* fetchThemesFlow() {
  yield* authedApiSaga(getChannelThemes, [
  ], fetchThemesSuccess, fetchThemesFailure);
}

export function* fetchModesFlow() {
  yield* authedApiSaga(getModes, [
  ], fetchModesSuccess, fetchModesFailure);
}

export default function* watchChannels() {
  yield all([
    takeLatest(FETCH_SLUG_VALIDATE_REQUEST, validateChannelSlugRequestFlow),
    takeLatest(FETCH_CHANNELS_REQUEST, fetchChannelsFlow),
    takeLatest(FETCH_UPDATED_CHANNEL_DETAIL_REQUEST, fetchUpdatedChannelDetailFlow),
    takeLatest(FETCH_CHANNEL_DETAIL_REQUEST, fetchChannelDetailFlow),
    takeLatest(FETCH_CLIENTS_REQUEST, fetchClientsFlow),
    takeLatest(FETCH_THEMES_REQUEST, fetchThemesFlow),
    takeLatest(FETCH_MODES_REQUEST, fetchModesFlow),
    takeLatest(POST_CHANNEL_REQUEST, postChannelFlow),
    takeLatest(PATCH_CHANNEL_REQUEST, patchChannelFlow),
    takeLatest(PUT_CHANNEL_FAILURE, channelFailureFlow),
    takeLatest(PUT_CHANNEL_SUCCESS, channelSuccessFlowForPost),
    takeLatest(PATCH_CHANNEL_SUCCESS, channelSuccessFlowForPatch),
    takeLatest(DELETE_CHANNEL_REQUEST, deleteChannelFlow),
    takeLatest(DELETE_CHANNEL_SUCCESS, deleteChannelSuccessFlow),
  ]);
}



// WEBPACK FOOTER //
// ./src/containers/ChannelsPage/sagas.js